// Third-party
import React, { ReactElement } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';

// Components
import DropdownMenu from './DropdownMenu';
import { WarningTooltip } from '../tooltips';
import ThemeMode from '@/types/theme-mode';
import SimpleDropdown from './SimpleDropdown';

export interface Props {
  onValueChange?: (value: any) => void;
  label?: string;
  link?: JSX.Element;
  disabled?: boolean;
  compact?: boolean;
  dark?: boolean;
  theme?: ThemeMode;
  selected: any; // Vague TODO - should this be only `string`?
  required?: boolean;
  className?: string;
  children: ReactElement[];
  placeholder: string;
  showWarning?: boolean;
  warningMessage?: string;
  horizontalLabel?: boolean;
  'data-testid'?: string;
  tooltip?: ReactElement;
  visibleCount?: number;
  upwards?: boolean;
  hasHiddenLabel?: boolean;
  'aria-label'?: string;
}

class SelectInput extends React.Component<Props> {
  uniqueId: string;

  static defaultProps = {
    placeholder: '',
  };

  constructor(props: Props) {
    super(props);
    this.uniqueId = uniqueId('select-input-');
  }

  getSelectedOption = () => {
    const { children, selected } = this.props;
    return children.find(o => isEqual(o.props.value, selected));
  };

  getSelectedLabel() {
    const { placeholder } = this.props;
    const option = this.getSelectedOption();
    return option ? option.props.label || option.props.children : placeholder;
  }

  getSelectedAriaLabel() {
    const option = this.getSelectedOption();
    return option && option.props['aria-label'];
  }

  getSelectedLang() {
    const option = this.getSelectedOption();
    return option && option.props.lang;
  }

  onSelectItem = (item: ReactElement) => {
    const { value } = item.props;
    const { selected, onValueChange } = this.props;

    if (selected !== value && onValueChange) {
      onValueChange(value);
    }
  };

  render() {
    const {
      label,
      link,
      compact,
      dark,
      theme,
      disabled,
      required,
      className,
      showWarning,
      warningMessage,
      onValueChange,
      horizontalLabel,
      'data-testid': testId,
      tooltip,
      visibleCount,
      upwards,
      hasHiddenLabel,
      'aria-label': ariaLabel,
      ...rest
    } = this.props;

    const classes = classNames('input select-input', className, {
      compact,
      dark,
      [`${theme}`]: theme,
      disabled,
      'horizontal-label': horizontalLabel,
      'has-no-value': !this.getSelectedOption(),
    });

    const selectedAriaLabel = this.getSelectedAriaLabel();

    return (
      <div className={classes} {...rest}>
        {(label || link) && (
          <div className={classNames('label-container', { 'has-tooltip': !!tooltip }, { 'visually-hidden': this.props.hasHiddenLabel })}>
            <label htmlFor={this.uniqueId}>
              {label}
              {required ? '*' : null}
            </label>

            {link && <label>{link}</label>}

            {!!tooltip && (
              <div className="toggle-switch-tooltip" id={`toggle-switch-${this.props['data-testid']}`}>
                {tooltip}
              </div>
            )}
          </div>
        )}

        <div className={classNames('content-container', { 'd-flex align-items-center': showWarning })}>
          {showWarning && warningMessage && <WarningTooltip message={warningMessage} />}

          <div className="input-container d-flex flex-cell">
            <SimpleDropdown
              id={this.uniqueId}
              visibleCount={visibleCount}
              disabled={disabled}
              options={this.props.children}
              onSelect={this.onSelectItem}
              data-testid={this.props['data-testid']}
              aria-label={ariaLabel}
              className={upwards ? 'open-upwards' : undefined}
            >
              <div className="selected-container">
                <div className="selected-label" lang={this.getSelectedLang()}>
                  {selectedAriaLabel && <span className="visually-hidden">{selectedAriaLabel}</span>}
                  <span aria-hidden={!!selectedAriaLabel}>{this.getSelectedLabel()}</span>
                </div>
                <i className="icon-cheveron-down" />
              </div>
            </SimpleDropdown>
          </div>
        </div>
      </div>
    );
  }
}

export default SelectInput;
